import { useState, useRef, useEffect } from 'react'
import { MessageCircle, X, Send, Sparkles, Mail, ArrowRight } from 'lucide-react'

// Pre-defined responses for common questions
const botResponses = {
    greeting: {
        text: "Hi there! 👋 I'm InkSpot's AI assistant. I can help you with questions about our features, pricing, or getting started. What would you like to know?",
        suggestions: ['Tell me about features', 'Show me pricing', 'How do I get started?']
    },
    features: {
        text: "InkSpot offers powerful AI-powered cliché detection! 🎯\n\n• **Real-time Detection**: Spots overused phrases as you write\n• **Smart Suggestions**: AI-powered alternatives that match your style\n• **2,000+ Patterns**: Our database catches common and subtle clichés\n• **Chrome Extension**: Works in Gmail, Google Docs, Twitter, LinkedIn & more\n• **Analytics Dashboard**: Track your writing improvement over time",
        suggestions: ['What about pricing?', 'How accurate is it?', 'Which platforms work?']
    },
    pricing: {
        text: "We offer flexible plans to fit your needs! 💎\n\n**Free Plan** - $0/month\n• 10 AI detections per day\n• Basic pattern matching\n• Chrome extension access\n\n**Pro Plan** - $7/month\n• Unlimited AI detections\n• Advanced suggestions\n• Custom style guides\n• Full analytics dashboard\n• Priority support",
        suggestions: ['Start for free', 'What\'s in Pro?', 'Team plans?']
    },
    getting_started: {
        text: "Getting started is super easy! 🚀\n\n1. **Sign up** for a free account\n2. **Install** our Chrome extension\n3. **Start writing** - we'll detect clichés in real-time!\n\nNo credit card required for the free plan.",
        suggestions: ['Sign up now', 'Tell me about features', 'View pricing']
    },
    accuracy: {
        text: "Our detection accuracy is over **95%** for common clichés! 📊\n\nWe combine pattern matching with Google's Gemini AI to catch both obvious and subtle overused phrases. Pro users can also adjust sensitivity levels to match their preferences.",
        suggestions: ['Show me features', 'How does AI work?', 'View pricing']
    },
    platforms: {
        text: "InkSpot works wherever you write! 💻\n\n• Gmail\n• Google Docs\n• Twitter/X\n• LinkedIn\n• Medium\n• WordPress\n• Most browser text editors\n\nOur web app also works standalone for drafting and editing.",
        suggestions: ['How do I install it?', 'Tell me about features', 'View pricing']
    },
    teams: {
        text: "We're working on team plans with exciting features! 👥\n\n• Shared style guides\n• Centralized billing\n• Admin controls\n\nInterested in an enterprise solution? Reach out to us at **support@inkspot.icu** and we'll create a custom plan for your team.",
        suggestions: ['Email support', 'View current pricing', 'Tell me about features']
    },
    pro_features: {
        text: "Pro unlocks the full InkSpot experience! ⭐\n\n• **Unlimited** AI-powered detections\n• **Advanced** contextual suggestions\n• **Custom** style guides for your brand voice\n• **Full** analytics dashboard\n• **Priority** email support\n• Cancel anytime with 30-day money-back guarantee",
        suggestions: ['Start Pro trial', 'Compare to Free', 'Ask another question']
    },
    default: {
        text: "I'm not sure I understand that question. Here are some topics I can help with:",
        suggestions: ['Tell me about features', 'Show me pricing', 'How do I get started?', 'Contact support']
    },
    contact: {
        text: "Need more help? Our support team is here for you! 📧\n\nEmail us at **support@inkspot.icu** and we'll get back to you within 24 hours.\n\nPro users get priority support with faster response times!",
        suggestions: ['Email support', 'View pricing', 'Tell me about features']
    }
}

// Simple keyword matching for responses
function getResponse(message) {
    const lower = message.toLowerCase()

    if (lower.includes('feature') || lower.includes('what can') || lower.includes('capabilities')) {
        return botResponses.features
    }
    if (lower.includes('price') || lower.includes('pricing') || lower.includes('cost') || lower.includes('how much')) {
        return botResponses.pricing
    }
    if (lower.includes('start') || lower.includes('begin') || lower.includes('sign up') || lower.includes('get started')) {
        return botResponses.getting_started
    }
    if (lower.includes('accurate') || lower.includes('accuracy') || lower.includes('reliable')) {
        return botResponses.accuracy
    }
    if (lower.includes('platform') || lower.includes('work with') || lower.includes('integrate') || lower.includes('extension')) {
        return botResponses.platforms
    }
    if (lower.includes('team') || lower.includes('enterprise') || lower.includes('company') || lower.includes('business')) {
        return botResponses.teams
    }
    if (lower.includes('pro') || lower.includes('premium') || lower.includes('upgrade')) {
        return botResponses.pro_features
    }
    if (lower.includes('contact') || lower.includes('support') || lower.includes('help') || lower.includes('email')) {
        return botResponses.contact
    }
    if (lower.includes('hi') || lower.includes('hello') || lower.includes('hey')) {
        return botResponses.greeting
    }

    return botResponses.default
}

function Message({ message, isUser }) {
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
            <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl ${isUser
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-br-md'
                    : 'bg-white/10 text-white/90 rounded-bl-md border border-white/10'
                    }`}
            >
                <p className="text-sm whitespace-pre-line">{message.text}</p>
            </div>
        </div>
    )
}

function TypingIndicator() {
    return (
        <div className="flex justify-start mb-3">
            <div className="bg-white/10 text-white/90 rounded-2xl rounded-bl-md border border-white/10 px-4 py-3">
                <div className="flex gap-1">
                    <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
            </div>
        </div>
    )
}

export default function ChatBot() {
    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState([
        { text: botResponses.greeting.text, isUser: false, suggestions: botResponses.greeting.suggestions }
    ])
    const [inputValue, setInputValue] = useState('')
    const [isTyping, setIsTyping] = useState(false)
    const messagesEndRef = useRef(null)
    const inputRef = useRef(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages, isTyping])

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    const handleSend = (text = inputValue) => {
        if (!text.trim()) return

        // Add user message
        setMessages(prev => [...prev, { text: text.trim(), isUser: true }])
        setInputValue('')
        setIsTyping(true)

        // Simulate typing delay
        setTimeout(() => {
            const response = getResponse(text)
            setMessages(prev => [...prev, {
                text: response.text,
                isUser: false,
                suggestions: response.suggestions
            }])
            setIsTyping(false)
        }, 800 + Math.random() * 400)
    }

    const handleSuggestionClick = (suggestion) => {
        if (suggestion === 'Email support') {
            window.location.href = 'mailto:support@inkspot.icu'
            return
        }
        if (suggestion === 'Sign up now' || suggestion === 'Start for free' || suggestion === 'Start Pro trial') {
            window.location.href = '/signup'
            return
        }
        handleSend(suggestion)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const lastMessage = messages[messages.length - 1]

    return (
        <>
            {/* Chat Window */}
            <div
                className={`fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 z-50 transition-all duration-300 transform origin-bottom-right ${isOpen
                    ? 'scale-100 opacity-100 pointer-events-auto'
                    : 'scale-95 opacity-0 pointer-events-none'
                    }`}
            >
                <div className="glass-card overflow-hidden shadow-2xl shadow-black/30 flex flex-col max-h-[80vh]">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-primary-500/20 to-accent-500/20 px-1 py-1 border-b border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <div className="font-semibold text-white text-sm">InkSpot Support</div>
                                <div className="text-xs text-white/50">AI Assistant</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
                            aria-label="Close chat"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar min-h-[200px] max-h-[40vh]">
                        {messages.map((message, index) => (
                            <Message key={index} message={message} isUser={message.isUser} />
                        ))}
                        {isTyping && <TypingIndicator />}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Suggestions */}
                    {!isTyping && lastMessage?.suggestions && (
                        <div className="px-4 pb-2 flex flex-wrap gap-2">
                            {lastMessage.suggestions.map((suggestion, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all flex items-center gap-1"
                                >
                                    {suggestion}
                                    <ArrowRight className="w-3 h-3" />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div className="p-3 border-t border-white/10 bg-white/5">
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask a question..."
                                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:bg-white/10 focus:border-primary-400 focus:outline-none transition-all text-sm"
                            />
                            <button
                                onClick={() => handleSend()}
                                disabled={!inputValue.trim() || isTyping}
                                className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed hover:from-primary-400 hover:to-accent-400 transition-all"
                                aria-label="Send message"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="mt-2 text-center">
                            <a
                                href="mailto:support@inkspot.icu"
                                className="text-xs text-white/40 hover:text-white/60 transition-colors inline-flex items-center gap-1"
                            >
                                <Mail className="w-3 h-3" />
                                Or email support@inkspot.icu
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-4 right-4 sm:right-6 z-50 w-14 h-14 rounded-full shadow-lg shadow-primary-500/30 flex items-center justify-center transition-all duration-300 transform hover:scale-110 ${isOpen
                    ? 'bg-white/10 backdrop-blur-xl border border-white/20 text-white'
                    : 'bg-gradient-to-r from-primary-500 to-accent-500 text-white'
                    }`}
                aria-label={isOpen ? 'Close chat' : 'Open chat'}
            >
                {isOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <MessageCircle className="w-6 h-6" />
                )}
            </button>
        </>
    )
}
